// The comment box puts one newline between blocks, so a list is followed directly by the next line of text. In
// Markdown a plain line under a list item belongs to that item, so "Test" typed after a list was drawn inside its
// last item - on the web, in Teams and in email alike. A blank line ends the list; it adds no visible gap, since
// what shows between the list and the text is the list's own margin.
//
// Only an unindented line ends the list: an indented one is the item's own continuation (Shift+Enter in an item).

const FENCE = /^ {0,3}(`{3,}|~{3,})/;
export const LIST_ITEM = /^ {0,3}([*+-]|\d{1,9}[.)])[ \t]/;

/**
 * The lines of `text`, with `null` marking each place a list has to be ended. With `endBeforeEmptyLines`, a list is
 * also ended before an empty line that follows it - for saving, where an empty line carries a zero-width space and
 * would otherwise be read as part of the last item, taking the text after it along.
 */
export const splitEndingLists = (text: string, endBeforeEmptyLines = false): (string | null)[] => {
  const out: (string | null)[] = [];
  let fenceOpen: string | null = null;
  let inList = false;
  for (const line of text.split('\n')) {
    const fence = FENCE.exec(line)?.[1];
    if (fence) {
      if (fenceOpen === null) fenceOpen = fence;
      else if (fence.startsWith(fenceOpen[0]) && fence.length >= fenceOpen.length && line.trim() === fence) fenceOpen = null;
    }
    if (fenceOpen !== null && !fence) {
      out.push(line);
      continue;
    }

    // Truly blank only. A line holding just a non-breaking space is text to Markdown, so it would join the item.
    const blank = /^[ \t]*$/.test(line);
    if (LIST_ITEM.test(line)) inList = true;
    else if (blank) {
      if (inList && endBeforeEmptyLines) out.push(null);
      inList = false;
    }
    else if (inList && !/^[ \t]/.test(line)) {
      out.push(null);
      inList = false;
    }
    out.push(line);
  }
  return out;
};

export const endListsBeforeText = (text: string): string =>
  splitEndingLists(text).map(line => line ?? '').join('\n');
